import React, { useState } from 'react';
import {
  Box, Tab, Tabs, Table, TableBody, TableCell, TableHead, TableRow,
  Typography, Link, Skeleton, ToggleButton, ToggleButtonGroup, Chip,
  Alert, Tooltip,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { OpenInNew, ArrowOutward, ArrowDownward } from '@mui/icons-material';
import { seoAPI } from '../../services/api';
import Panel from '../crt/Panel';

/**
 * BACKLINKS panel — Bing Webmaster Tools data.
 *
 * Why Bing instead of GSC? GSC's API does not expose link data; Top
 * linking sites is web-UI only. Bing Webmaster Tools DOES expose
 * inbound links via API, and Bing's link graph has ~80% overlap with
 * Google's. So Bing is the only practical API path for link
 * monitoring without paying for Ahrefs/Moz/Majestic.
 *
 * Panels:
 *   • Top referring domains  — who links to us most
 *   • Top linked pages       — our pages with the most inbound links
 *   • New links              — appeared since last snapshot (good)
 *   • Lost links             — were there last snapshot, gone now (investigate)
 *
 * Snapshot cadence: weekly (link graphs change slowly).
 */

const fmt = (n: number | null | undefined) =>
  n == null ? '—' : Number(n).toLocaleString();

const BacklinksPanel: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [site, setSite] = useState<string>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['seo-backlinks', site],
    queryFn: () => seoAPI.getBacklinksSummary({
      site: site === 'all' ? undefined : site,
    }),
    staleTime: 30 * 60_000,  // weekly cron — no need to refetch often
  });

  const domains   = data?.top_referring_domains || [];
  const pages     = data?.top_linked_pages      || [];
  const newLinks  = data?.new_links             || [];
  const lostLinks = data?.lost_links            || [];

  return (
    <Panel label="BACKLINKS :: BING WEBMASTER TOOLS">
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary', flexGrow: 1 }}>
            Inbound link graph from Bing WMT (Google's link API doesn't exist).
            {data?.latest_snapshot && (
              <> Latest snapshot: <strong>{data.latest_snapshot}</strong>
                {data.previous_snapshot && <> · prior: {data.previous_snapshot}</>}.</>
            )}
          </Typography>
          <ToggleButtonGroup size="small" exclusive value={site} onChange={(_, v) => v && setSite(v)}>
            <ToggleButton value="all">all</ToggleButton>
            <ToggleButton value="spoutlore">SL</ToggleButton>
            <ToggleButton value="mallbrats">MB</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {isLoading && <Skeleton variant="rectangular" height={300} />}

        {!isLoading && data && !data.available && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Backlinks integration ready, awaiting setup
            </Typography>
            <Typography variant="caption" sx={{ display: 'block' }}>
              {data.reason}
            </Typography>
            <Box component="ol" sx={{ mt: 1, pl: 2, fontSize: 13 }}>
              <li>Sign up at <Link href="https://www.bing.com/webmasters/" target="_blank" rel="noopener noreferrer">bing.com/webmasters</Link></li>
              <li>Add + verify <code>spoutlore.com</code> and <code>mallbratspodcast.com</code> (meta-tag method, ~5 min)</li>
              <li>Settings → API Access → generate API key</li>
              <li>Set <code>BING_WEBMASTER_API_KEY</code> in <code>/opt/autopod/.env</code></li>
              <li>Run: <code>docker exec autopod-backend python -m etl.cli.bing_links_sync --apply</code></li>
            </Box>
          </Alert>
        )}

        {!isLoading && data?.available && (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tab label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>🌐 Top domains</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {domains.length} referring
                  </Typography>
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>📄 Top pages</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {pages.length} linked
                  </Typography>
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>↗ New</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {newLinks.length} since last snapshot
                  </Typography>
                </Box>
              } />
              <Tab label={
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: 'error.main' }}>↘ Lost</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {lostLinks.length} disappeared
                  </Typography>
                </Box>
              } />
            </Tabs>

            {/* Top referring domains */}
            {tab === 0 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Domain</TableCell>
                      <TableCell align="right">Links</TableCell>
                      <TableCell align="right">Pages they hit</TableCell>
                      <TableCell>First seen</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {domains.map((d: any) => (
                      <TableRow key={d.domain} hover>
                        <TableCell>
                          <Link href={`https://${d.domain}`} target="_blank" rel="noopener noreferrer"
                                sx={{ color: 'inherit', fontFamily: 'monospace', fontSize: 13 }}>
                            {d.domain} <OpenInNew fontSize="inherit" />
                          </Link>
                        </TableCell>
                        <TableCell align="right">{fmt(d.link_count)}</TableCell>
                        <TableCell align="right">{fmt(d.pages_targeted)}</TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {d.first_seen ? new Date(d.first_seen).toLocaleDateString() : '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {domains.length === 0 && (
                      <TableRow><TableCell colSpan={4}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>No data yet.</Typography>
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Top linked pages */}
            {tab === 1 && (
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Page</TableCell>
                      <TableCell align="right">Total links</TableCell>
                      <TableCell align="right">Unique domains</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pages.map((p: any) => {
                      const path = (p.page || '').replace(/^https?:\/\/[^/]+/, '') || '/';
                      return (
                        <TableRow key={p.page} hover>
                          <TableCell sx={{ maxWidth: 480 }}>
                            <Tooltip title={p.page}>
                              <Link href={p.page} target="_blank" rel="noopener noreferrer"
                                    sx={{ color: 'inherit', fontFamily: 'monospace', fontSize: 12,
                                          whiteSpace: 'nowrap', overflow: 'hidden',
                                          textOverflow: 'ellipsis', display: 'block' }}>
                                {path} <OpenInNew fontSize="inherit" />
                              </Link>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">{fmt(p.link_count)}</TableCell>
                          <TableCell align="right">{fmt(p.unique_domains)}</TableCell>
                        </TableRow>
                      );
                    })}
                    {pages.length === 0 && (
                      <TableRow><TableCell colSpan={3}>
                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>No data yet.</Typography>
                      </TableCell></TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* New links */}
            {tab === 2 && (
              <NewOrLostTable rows={newLinks} kind="new" />
            )}

            {/* Lost links */}
            {tab === 3 && (
              <NewOrLostTable rows={lostLinks} kind="lost" />
            )}
          </>
        )}
      </Box>
    </Panel>
  );
};


const NewOrLostTable: React.FC<{ rows: any[]; kind: 'new' | 'lost' }> = ({ rows, kind }) => {
  if (rows.length === 0) {
    return (
      <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', py: 4, color: 'text.secondary' }}>
        {kind === 'new' ? 'No new inbound links since last snapshot.' : 'No links lost since last snapshot — nice.'}
      </Typography>
    );
  }
  const Icon = kind === 'new' ? ArrowOutward : ArrowDownward;
  const color = kind === 'new' ? 'success.main' : 'error.main';
  return (
    <>
      <Typography variant="caption" sx={{ display: 'block', mb: 1, color: 'text.secondary' }}>
        {kind === 'new'
          ? 'Links that appeared since last snapshot. If a notable site, send them a thank-you / engage with their post.'
          : "Links that vanished since last snapshot. Could be the source page deleted, or the source removed the link. Worth a manual check on big ones."}
      </Typography>
      <Box sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Anchor</TableCell>
              <TableCell>To page</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r: any, i: number) => {
              const targetPath = (r.target_url || '').replace(/^https?:\/\/[^/]+/, '') || '/';
              return (
                <TableRow key={`${r.source_url}-${r.target_url}-${i}`} hover>
                  <TableCell><Icon sx={{ color, fontSize: 18 }} /></TableCell>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <Tooltip title={r.source_url}>
                      <Link href={r.source_url} target="_blank" rel="noopener noreferrer"
                            sx={{ color: 'inherit', fontFamily: 'monospace', fontSize: 12,
                                  whiteSpace: 'nowrap', overflow: 'hidden',
                                  textOverflow: 'ellipsis', display: 'block' }}>
                        {r.source_domain}
                      </Link>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontStyle: 'italic' }}>
                      {r.anchor_text || '(no anchor)'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 240 }}>
                    <Tooltip title={r.target_url}>
                      <Typography variant="caption" sx={{
                        fontFamily: 'monospace', whiteSpace: 'nowrap',
                        overflow: 'hidden', textOverflow: 'ellipsis', display: 'block',
                      }}>
                        {targetPath}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </>
  );
};


export default BacklinksPanel;
